import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';
import { useAuthContext } from '../contexts/AuthContext';
import { businessService } from '../services/businessService';
import { userService } from '../services/userService';
import { stripeService, PACKAGES } from '../services/stripeService';
import { Timestamp, doc, updateDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { ArrowRightIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import AdditionalInformationForm, { AdditionalInformationFormData } from '../components/AdditionalInformationForm';
import { uploadFile, deleteFile } from '../services/storageService';
import { isImageFile, isDocumentFile } from '../utils/imageUtils';
import { upsertFranchiseListing, removeFranchiseListing } from '../services/franchiseService';

interface FormData {
  // Step 1: Build Your Listing
  listingHeadline: string;
  generalSummary: string;
  statusOfBusiness: 'for_sale' | 'under_offer' | 'sold_subject_to_contract';
  categories: string[];
  region: string;
  cityTown: string;
  propertyStatus: 'real_property' | 'lease' | 'both' | 'na';
  askingPriceRange: string;
  askingPriceSpecific: string;
  askingPriceOnRequest: boolean;
  quickSale: boolean;
  salesRevenueRange: string;
  salesRevenueSpecific: string;
  salesRevenueOnRequest: boolean;
  cashFlowRange: string;
  cashFlowSpecific: string;
  cashFlowOnRequest: boolean;
  websiteAddress: string;
  
  // Step 2: Additional Information
  location: string;
  competition: string;
  expansionPotential: string;
  yearsEstablished: string;
  employees: string;
  tradingHours: string;
  supportTraining: string;
  relocatable: 'yes' | 'no' | '';
  workFromHome: boolean;
  franchise: boolean;
  assetSale: boolean;
  financingAvailable: boolean;
  leaseTerms: string;
  reasonForSale: string;
  
  // Step 3: Contact Information
  contactName: string;
  contactEmail: string;
  confirmEmail: string;
  contactPhone: string;
  password: string;
  confirmPassword: string;
}

const SellBusiness: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const params = useParams();
  const { currentUser, userProfile, loading, signIn, signUp } = useAuthContext();
  const [currentStep, setCurrentStep] = useState(1);
  const [savedListingId, setSavedListingId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState<'CAD' | 'USD'>('CAD');
  const [formData, setFormData] = useState<FormData>({
    listingHeadline: '',
    generalSummary: '',
    statusOfBusiness: 'for_sale',
    categories: [],
    region: '',
    cityTown: '',
    propertyStatus: 'na',
    askingPriceRange: '',
    askingPriceSpecific: '',
    askingPriceOnRequest: false,
    quickSale: false,
    salesRevenueRange: '',
    salesRevenueSpecific: '',
    salesRevenueOnRequest: false,
    cashFlowRange: '',
    cashFlowSpecific: '',
    cashFlowOnRequest: false,
    websiteAddress: '',
    location: '',
    competition: '',
    expansionPotential: '',
    yearsEstablished: '',
    employees: '',
    tradingHours: '',
    supportTraining: '',
    relocatable: '',
    workFromHome: false,
    franchise: false,
    assetSale: false,
    financingAvailable: false,
    leaseTerms: '',
    reasonForSale: '',
    contactName: '',
    contactEmail: '',
    confirmEmail: '',
    contactPhone: '',
    password: '',
    confirmPassword: ''
  });

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadedPhotos, setUploadedPhotos] = useState<string[]>([]);
  const [uploadedDocuments, setUploadedDocuments] = useState<string[]>([]);
  const [emailError, setEmailError] = useState<string>('');
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [signInEmail, setSignInEmail] = useState<string>('');
  const [signInPassword, setSignInPassword] = useState<string>('');
  const [signInError, setSignInError] = useState<string>('');
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [contactOption, setContactOption] = useState<'login' | 'create_account'>('create_account');
  const paymentProcessedRef = useRef<string | null>(null);
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const [countdown, setCountdown] = useState(15);
  const countdownTimerRef = useRef<NodeJS.Timeout | null>(null);

  // SEO
  const seoTitle = "Sell Your Business | BizBuyMax.com - List Your Business for Sale";
  usePageTitle(seoTitle);

  // Allow non-registered users - removed authentication check

  // Load existing draft if draft ID is provided in URL
  useEffect(() => {
    const loadExistingDraft = async () => {
      const draftId = searchParams.get('draft');
      const editId = params.id || searchParams.get('edit'); // Get ID from route params or query string
      const listingId = draftId || editId;
      
      if (listingId && currentUser) {
        try {
          const draft = await businessService.getBusiness(listingId);
          // Allow 'draft', 'pending_payment', and 'active' status for editing
          const draftStatus = draft?.basic.status;
          if (draft && draft.owner.userId === currentUser.uid && 
              (draftStatus === 'draft' || draftStatus === 'pending_payment' || draftStatus === 'active')) {
            setSavedListingId(listingId);
            
            // Populate form with draft data
            setFormData(prev => ({
              ...prev,
              listingHeadline: draft.basic.title || '',
              generalSummary: draft.basic.description || '',
              categories: draft.basic.tags || [],
              region: draft.location.region || '',
              cityTown: draft.location.city || '',
              askingPriceSpecific: draft.financial.askingPrice?.toString() || '',
              salesRevenueSpecific: draft.financial.annualRevenue?.toString() || '',
              cashFlowSpecific: draft.financial.annualCashFlow?.toString() || '',
              websiteAddress: (draft as any).websiteAddress || '',
              location: (draft.business as any)?.location || '',
              competition: (draft.business as any)?.competition || '',
              expansionPotential: (draft.business as any)?.expansionPotential || '',
              yearsEstablished: draft.business?.established?.toString() || '',
              employees: draft.business?.employees ? `${draft.business.employees.min || 0}-${draft.business.employees.max || 0}` : '',
              tradingHours: (draft.business as any)?.tradingHours || '',
              supportTraining: (draft.business as any)?.supportTraining || '',
              relocatable: draft.business?.relocatable ? 'yes' : (draft.business?.relocatable === false ? 'no' : ''),
              workFromHome: draft.business?.workFromHome || false,
              franchise: draft.business?.franchise || false,
              assetSale: (draft.business as any)?.assetSale || false,
              financingAvailable: draft.financial?.financingAvailable || false,
              leaseTerms: draft.business?.leaseTerms || '',
              reasonForSale: draft.business?.reasonForSale || '',
              contactName: draft.owner.contactInfo.name || '',
              contactEmail: draft.owner.contactInfo.email || '',
              confirmEmail: draft.owner.contactInfo.email || '',
              contactPhone: draft.owner.contactInfo.phone || ''
            }));
            
            setUploadedPhotos(draft.media.images || []);
            setUploadedDocuments(draft.media.documents || []);
            
            // If payment is completed or listing is active, go directly to step 3 (Additional Information)
            if ((draft as any).payment?.completed || draftStatus === 'active') {
              setCurrentStep(3);
            }
            
            // console.log('Loaded existing draft:', listingId);
          }
        } catch (error) {
          console.error('Error loading draft:', error);
        }
      }
    };

    if (currentUser && !loading) {
      loadExistingDraft();
    }
  }, [currentUser, loading, searchParams, params.id]);

  // Function to load draft and go to step 3
  const loadDraftAndGoToStep3 = useCallback(async (draftId: string) => {
    try {
      setSavedListingId(draftId);
      
      // First, verify payment status (fallback if webhook hasn't fired)
      try {
        const verificationResult = await stripeService.verifyPayment(draftId);
        if (verificationResult.success) {
          console.log('Payment verified:', verificationResult.message);
          if (verificationResult.alreadyCompleted) {
            console.log('Payment was already processed');
          }
        } else {
          console.warn('Payment verification failed:', verificationResult.message);
        }
      } catch (verifyError) {
        console.error('Error verifying payment (non-fatal):', verifyError);
        // Continue even if verification fails - webhook might handle it
      }
      
      // Load the draft to populate form data
      const draft = await businessService.getBusiness(draftId);
      if (draft && draft.owner.userId === currentUser?.uid) {
        // Populate form with draft data
        setFormData(prev => ({
          ...prev,
          listingHeadline: draft.basic.title || '',
          generalSummary: draft.basic.description || '',
          categories: draft.basic.tags || [],
          region: draft.location.region || '',
          cityTown: draft.location.city || '',
          askingPriceSpecific: draft.financial.askingPrice?.toString() || '',
          salesRevenueSpecific: draft.financial.annualRevenue?.toString() || '',
          cashFlowSpecific: draft.financial.annualCashFlow?.toString() || '',
          websiteAddress: (draft as any).websiteAddress || '',
          location: (draft.business as any)?.location || '',
          competition: (draft.business as any)?.competition || '',
          expansionPotential: (draft.business as any)?.expansionPotential || '',
          yearsEstablished: draft.business?.established?.toString() || '',
          employees: draft.business?.employees ? `${draft.business.employees.min || 0}-${draft.business.employees.max || 0}` : '',
          tradingHours: (draft.business as any)?.tradingHours || '',
          supportTraining: (draft.business as any)?.supportTraining || '',
          relocatable: draft.business?.relocatable ? 'yes' : (draft.business?.relocatable === false ? 'no' : ''),
          workFromHome: draft.business?.workFromHome || false,
          franchise: draft.business?.franchise || false,
          assetSale: (draft.business as any)?.assetSale || false,
          financingAvailable: draft.financial?.financingAvailable || false,
          leaseTerms: draft.business?.leaseTerms || '',
          reasonForSale: draft.business?.reasonForSale || '',
          contactName: draft.owner.contactInfo.name || '',
          contactEmail: draft.owner.contactInfo.email || '',
          confirmEmail: draft.owner.contactInfo.email || '',
          contactPhone: draft.owner.contactInfo.phone || ''
        }));
        
        setUploadedPhotos(draft.media.images || []);
        setUploadedDocuments(draft.media.documents || []);
        
        // Navigate to step 3 (Additional Information)
        setCurrentStep(3);
        setShowPaymentSuccess(false);
        
        // Remove payment parameters from URL to prevent re-triggering
        const newUrl = window.location.pathname + '?draft=' + draftId;
        window.history.replaceState({}, document.title, newUrl);
      }
    } catch (error) {
      console.error('Error loading draft after payment:', error);
      // Silently handle error - no alert needed
    }
  }, [currentUser]);

  // Handle payment success/cancel redirects
  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    const draftId = searchParams.get('draft');
    
    // Create a unique key for this payment event
    const paymentKey = `${paymentStatus}-${draftId}`;
    
    // Skip if we've already processed this payment event
    if (paymentProcessedRef.current === paymentKey) {
      return;
    }
    
    if (paymentStatus === 'success' && draftId && currentUser && !loading) {
      // Mark as processed immediately to prevent duplicate runs
      paymentProcessedRef.current = paymentKey;
      
      // Show payment success page instead of going directly to step 3
      setShowPaymentSuccess(true);
      setCountdown(15);
      
      // Remove payment parameters from URL to prevent re-triggering
      const newUrl = window.location.pathname + '?draft=' + draftId;
      window.history.replaceState({}, document.title, newUrl);
    } else if (paymentStatus === 'cancelled' && draftId && !loading) {
      // Mark as processed immediately to prevent duplicate runs
      paymentProcessedRef.current = paymentKey;
      
      setSavedListingId(draftId);
      // Remove payment parameters from URL to prevent re-triggering
      const newUrl = window.location.pathname + '?draft=' + draftId;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, [searchParams, currentUser, loading]);

  // Handle countdown timer for payment success page
  useEffect(() => {
    if (showPaymentSuccess && countdown > 0) {
      countdownTimerRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            // Timer expired, go to step 3
            const draftId = searchParams.get('draft');
            if (draftId) {
              loadDraftAndGoToStep3(draftId);
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }
    };
  }, [showPaymentSuccess, countdown, searchParams, loadDraftAndGoToStep3]);

  // Prefill contact information with user profile data (only if logged in)
  useEffect(() => {
    if (userProfile && currentUser) {
      setFormData(prev => ({
        ...prev,
        contactName: `${userProfile.profile.firstName} ${userProfile.profile.lastName}`,
        contactEmail: userProfile.profile.email,
        confirmEmail: userProfile.profile.email, // Set confirmEmail to same value for logged in users
        contactPhone: userProfile.profile.phone || ''
      }));
    }
  }, [userProfile, currentUser]);

  // Check if email already exists in database
  const checkEmailExists = async (email: string) => {
    if (!email || email.length < 3) {
      setEmailError('');
      return false;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError('Please enter a valid email address');
      return false;
    }

    setIsCheckingEmail(true);
    try {
      const existingUser = await userService.getUserByEmail(email);
      if (existingUser) {
        setEmailError('This email is already registered. Please sign in or use a different email.');
        setIsCheckingEmail(false);
        return true;
      } else {
        setEmailError('');
        setIsCheckingEmail(false);
        return false;
      }
    } catch (error) {
      console.error('Error checking email:', error);
      setEmailError('Error checking email. Please try again.');
      setIsCheckingEmail(false);
      return false;
    }
  };

  // Handle sign in
  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setSignInError('');
    setIsSigningIn(true);

    if (!signInEmail.trim() || !signInPassword.trim()) {
      setSignInError('Please enter both email and password');
      setIsSigningIn(false);
      return;
    }

    try {
      await signIn(signInEmail.trim(), signInPassword);
      // Refresh the page after successful sign-in
      window.location.reload();
    } catch (error: any) {
      setSignInError(error.message || 'Sign in failed. Please check your credentials.');
      setIsSigningIn(false);
    }
  };

  const handleInputChange = (field: keyof FormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCategoryChange = (category: string) => {
    setFormData(prev => {
      const categories = prev.categories.includes(category)
        ? prev.categories.filter(c => c !== category)
        : prev.categories.length < 3 
          ? [...prev.categories, category]
          : prev.categories;
      return { ...prev, categories };
    });
  };

  const removeCategory = (category: string) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.filter(c => c !== category)
    }));
  };

  const handleRemovePhoto = async (index: number) => {
    if (index < 0 || index >= uploadedPhotos.length) return;

    const photoUrl = uploadedPhotos[index];
    
    try {
      // Remove from state first for immediate UI update
      setUploadedPhotos(prev => prev.filter((_, i) => i !== index));
      
      // Try to delete from Firebase Storage (don't fail if it doesn't exist)
      try {
        await deleteFile(photoUrl);
      } catch (error) {
        console.warn('Failed to delete photo from storage:', error);
        // Continue even if deletion fails
      }
    } catch (error: any) {
      console.error('Error removing photo:', error);
      alert('Failed to remove photo. Please try again.');
      // Re-add to state if removal failed
      setUploadedPhotos(prev => {
        const newPhotos = [...prev];
        newPhotos.splice(index, 0, photoUrl);
        return newPhotos;
      });
    }
  };

  const handleRemoveDocument = async (index: number) => {
    if (index < 0 || index >= uploadedDocuments.length) return;

    const docUrl = uploadedDocuments[index];
    
    try {
      // Remove from state first for immediate UI update
      setUploadedDocuments(prev => prev.filter((_, i) => i !== index));
      
      // Try to delete from Firebase Storage (don't fail if it doesn't exist)
      try {
        await deleteFile(docUrl);
      } catch (error) {
        console.warn('Failed to delete document from storage:', error);
        // Continue even if deletion fails
      }
    } catch (error: any) {
      console.error('Error removing document:', error);
      alert('Failed to remove document. Please try again.');
      // Re-add to state if removal failed
      setUploadedDocuments(prev => {
        const newDocs = [...prev];
        newDocs.splice(index, 0, docUrl);
        return newDocs;
      });
    }
  };

  const handleFileUpload = async (type: 'photo' | 'document') => {
    if (!selectedFile) return;

    // Ensure user is authenticated
    if (!currentUser) {
      alert('Please sign in to upload files');
      setSelectedFile(null);
      return;
    }

    // Validate file type
    if (type === 'photo' && !isImageFile(selectedFile)) {
      alert('Please select a valid image file');
      setSelectedFile(null);
      return;
    }

    if (type === 'document' && !isDocumentFile(selectedFile)) {
      alert('Please select a valid document file (PDF, Word, Excel)');
      setSelectedFile(null);
      return;
    }

    // Validate file count limits
    if (type === 'photo' && uploadedPhotos.length >= 15) {
      alert('Maximum 15 photos allowed');
      setSelectedFile(null);
      return;
    }

    if (type === 'document' && uploadedDocuments.length >= 3) {
      alert('Maximum 3 documents allowed');
      setSelectedFile(null);
      return;
    }

    try {
      setIsSaving(true);

      // Determine storage path based on whether we have a saved listing ID
      const userId = currentUser.uid;
      const listingId = savedListingId || 'draft';
      const storagePath = type === 'photo' 
        ? `listings/${userId}/${listingId}/images/`
        : `listings/${userId}/${listingId}/documents/`;

      // Upload file to Firebase Storage
      const downloadURL = await uploadFile(selectedFile, storagePath, type === 'photo');

      // Update state with download URL
      if (type === 'photo') {
        setUploadedPhotos(prev => [...prev, downloadURL]);
      } else {
        setUploadedDocuments(prev => [...prev, downloadURL]);
      }

      setSelectedFile(null);
    } catch (error: any) {
      console.error('Error uploading file:', error);
      alert(`Failed to upload file: ${error.message || 'Please try again'}`);
    } finally {
      setIsSaving(false);
    }
  };

  const createDraftWithPendingPayment = async (userId: string): Promise<string> => {
    // First, check if there's an existing draft with pending_payment status for this user
    try {
      const userListings = await businessService.getBusinessesByOwner(userId);
      const existingPendingDraft = userListings.find(
        (listing: any) => listing.basic?.status === 'pending_payment'
      );

      if (existingPendingDraft && existingPendingDraft.id) {
        // Update existing draft instead of creating a new one
        console.log('Found existing pending_payment draft, updating:', existingPendingDraft.id);

        // Update the existing draft using flattened Firestore update format
        const updateData: any = {
          'basic.title': formData.listingHeadline || existingPendingDraft.basic.title || 'Draft Listing',
          'basic.description': formData.generalSummary || existingPendingDraft.basic.description || '',
          'basic.shortDescription': (formData.generalSummary || existingPendingDraft.basic.description || '').substring(0, 200),
          'basic.category': formData.categories[0] || existingPendingDraft.basic.category || 'Other',
          'basic.subcategory': formData.categories[1] || existingPendingDraft.basic.subcategory || '',
          'basic.tags': formData.categories.length > 0 ? formData.categories : existingPendingDraft.basic.tags || [],
          'basic.status': 'pending_payment',
          'basic.updatedAt': Timestamp.now(),
          'location.city': formData.cityTown || existingPendingDraft.location?.city || '',
          'location.province': formData.region || existingPendingDraft.location?.province || '',
          'location.region': formData.region || existingPendingDraft.location?.region || '',
          'financial.askingPrice': formData.askingPriceSpecific ? parseFloat(formData.askingPriceSpecific) : (existingPendingDraft.financial?.askingPrice || 0),
          'media.images': uploadedPhotos.length > 0 ? uploadedPhotos : (existingPendingDraft.media?.images || []),
          'media.documents': uploadedDocuments.length > 0 ? uploadedDocuments : (existingPendingDraft.media?.documents || []),
          'media.featuredImage': uploadedPhotos[0] || existingPendingDraft.media?.featuredImage || '',
          'owner.contactInfo.name': formData.contactName || existingPendingDraft.owner?.contactInfo?.name || '',
          'owner.contactInfo.email': formData.contactEmail || existingPendingDraft.owner?.contactInfo?.email || '',
          'owner.contactInfo.phone': formData.contactPhone || existingPendingDraft.owner?.contactInfo?.phone || '',
          'search.searchableText': `${formData.listingHeadline || ''} ${formData.generalSummary || ''}`,
          'search.locationKeywords': [formData.region || '', formData.cityTown || ''],
          'search.categoryKeywords': formData.categories.length > 0 ? formData.categories : (existingPendingDraft.search?.categoryKeywords || [])
        };

        const docRef = doc(db, 'businesses', existingPendingDraft.id);
        await updateDoc(docRef, updateData);
        
        return existingPendingDraft.id;
      }
    } catch (error) {
      console.error('Error checking for existing drafts:', error);
      // Continue to create new draft if check fails
    }

    // No existing pending_payment draft found, create a new one
    const draftData = {
      basic: {
        title: formData.listingHeadline || 'Draft Listing',
        description: formData.generalSummary || '',
        shortDescription: (formData.generalSummary || '').substring(0, 200),
        category: formData.categories[0] || 'Other',
        subcategory: formData.categories[1] || '',
        tags: formData.categories,
        status: 'pending_payment' as const, // Status before payment
        featured: false,
        verified: false,
        expiresAt: Timestamp.fromDate(new Date(Date.now() + 365 * 24 * 60 * 60 * 1000))
      },
      location: {
        address: '',
        city: formData.cityTown || '',
        province: formData.region || '',
        postalCode: '',
        country: 'Canada',
        coordinates: { latitude: 0, longitude: 0 } as any,
        region: formData.region || '',
        timezone: 'America/Toronto'
      },
      financial: {
        askingPrice: formData.askingPriceSpecific ? parseFloat(formData.askingPriceSpecific) : 0,
        currency: 'CAD',
        annualRevenue: 0,
        annualCashFlow: 0,
        annualExpenses: 0,
        profitMargin: 0,
        growthRate: 0,
        pricePerRevenue: 0,
        pricePerCashFlow: 0,
        financingAvailable: false
      },
      business: {
        established: 0,
        employees: { min: 0, max: 0 },
        squareFootage: 0,
        leaseTerms: '',
        reasonForSale: '',
        trainingIncluded: false,
        relocatable: false,
        workFromHome: false,
        absenteeOwnership: false,
        franchise: false
      },
      media: {
        images: uploadedPhotos,
        videos: [],
        documents: uploadedDocuments,
        featuredImage: uploadedPhotos[0] || ''
      },
      owner: {
        userId: userId,
        contactInfo: {
          name: formData.contactName || '',
          email: formData.contactEmail || '',
          phone: formData.contactPhone || '',
          preferredContact: 'email' as const
        },
        responseTime: 24
      },
      search: {
        searchableText: `${formData.listingHeadline || ''} ${formData.generalSummary || ''}`,
        priceRange: 'under_100k' as const,
        revenueRange: 'under_100k' as const,
        locationKeywords: [formData.region || '', formData.cityTown || ''],
        categoryKeywords: formData.categories
      }
    };

    const listingId = await businessService.createBusiness(draftData as any);
    return listingId;
  };

  const nextStep = async () => {
    if (currentStep === 1) {
      // Validate required fields for step 1
      if (!formData.listingHeadline.trim() || 
          !selectedPackage) {
        alert('Please fill in all required fields');
        return;
      }

      // Validate contact fields based on user status
      if (!currentUser && contactOption === 'create_account') {
        if (!formData.contactName.trim() ||
            !formData.contactEmail.trim() ||
            !formData.contactPhone.trim() ||
            !formData.confirmEmail.trim() ||
            formData.contactEmail !== formData.confirmEmail ||
            !formData.password.trim() ||
            formData.password.length < 6 ||
            !formData.confirmPassword.trim() ||
            formData.password !== formData.confirmPassword ||
            !!emailError) {
          alert('Please fix all account creation errors');
          return;
        }
      } else if (!currentUser && contactOption === 'login') {
        if (!formData.contactName.trim() ||
            !formData.contactEmail.trim() ||
            !formData.contactPhone.trim()) {
          alert('Please fill in all required fields');
          return;
        }
      } else if (currentUser) {
        // For signed-in users, only require name and phone
        if (!formData.contactName.trim() ||
            !formData.contactPhone.trim()) {
          alert('Please fill in all required fields');
          return;
        }
      }

      setIsSaving(true);
      
      try {
        let userId = currentUser?.uid;
        
        // Create user account if not logged in
        if (!currentUser && contactOption === 'create_account') {
          try {
            const nameParts = formData.contactName.trim().split(' ');
            const firstName = nameParts[0] || '';
            const lastName = nameParts.slice(1).join(' ') || '';
            
            const signUpResult = await signUp(
              formData.contactEmail.trim(),
              formData.password,
              {
                profile: {
                  email: formData.contactEmail.trim(),
                  firstName,
                  lastName,
                  phone: formData.contactPhone.trim(),
                  userType: 'seller',
                  avatar: '',
                  location: {
                    city: formData.cityTown || '',
                    province: formData.region || '',
                    country: 'Canada',
                    coordinates: { latitude: 0, longitude: 0 } as any
                  },
                  preferences: {
                    categories: [],
                    priceRange: { min: 0, max: 0 },
                    locations: [],
                    emailNotifications: true
                  },
                  verification: {
                    emailVerified: false,
                    phoneVerified: false,
                    identityVerified: false
                  }
                },
                settings: {
                  privacy: {
                    showContactInfo: true,
                    showProfile: true,
                    allowMessages: true
                  },
                  notifications: {
                    email: true,
                    push: false,
                    sms: false,
                    marketing: true
                  },
                  language: 'en'
                }
              }
            );
            
            userId = signUpResult.user.uid;
          } catch (error: any) {
            console.error('Error creating user:', error);
            alert('Error creating account: ' + (error.message || 'Please try again'));
            setIsSaving(false);
            return;
          }
        } else if (!currentUser && contactOption === 'login') {
          alert('Please sign in first');
          setIsSaving(false);
          return;
        }

        if (!userId) {
          alert('User ID is required');
          setIsSaving(false);
          return;
        }

        // Create draft with pending_payment status
        const listingId = await createDraftWithPendingPayment(userId);
        setSavedListingId(listingId);

        // Get customer email - prioritize logged-in user's email, then form email
        let customerEmail: string | undefined;
        if (currentUser?.email) {
          customerEmail = currentUser.email;
        } else if (userProfile?.profile?.email) {
          customerEmail = userProfile.profile.email;
        } else if (formData.contactEmail) {
          customerEmail = formData.contactEmail;
        }

        // Process payment
        const baseUrl = window.location.origin;
        const successUrl = `${baseUrl}/sell-business?payment=success&draft=${listingId}`;
        const cancelUrl = `${baseUrl}/sell-business?payment=cancelled&draft=${listingId}`;

        await stripeService.processPayment(
          selectedPackage,
          listingId,
          userId,
          selectedCurrency,
          successUrl,
          cancelUrl,
          customerEmail
        );
      } catch (error: any) {
        console.error('Error processing payment:', error);
        alert('Error processing payment: ' + (error.message || 'Please try again'));
        setIsSaving(false);
      }
    } else if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      // If on step 3, go back to step 1 (skip step 2)
      if (currentStep === 3) {
        setCurrentStep(1);
      } else {
        setCurrentStep(currentStep - 1);
      }
    }
  };

  const submitListing = async () => {
    if (!savedListingId) {
      alert('Error: No listing found. Please start over.');
      return;
    }

    if (!currentUser) {
      alert('Please sign in to submit your listing.');
      return;
    }

    try {
      setIsSubmitting(true);

      // Get the current listing to preserve package and payment info
      const listing = await businessService.getBusiness(savedListingId);
      if (!listing || listing.owner.userId !== currentUser.uid) {
        alert('Error: Listing not found or you do not have permission to edit it.');
        setIsSubmitting(false);
        return;
      }

      // Check if payment was completed - if not, don't allow submission (unless it's an active listing being edited)
      const isActiveListing = listing.basic.status === 'active';
      if (!isActiveListing && !(listing as any).payment?.completed) {
        alert('Please complete payment before submitting your listing.');
        setIsSubmitting(false);
        return;
      }

      // Use expiration date from package if available, otherwise use existing expiresAt
      const expiresAt = (listing as any).package?.expiresAt || listing.basic.expiresAt || Timestamp.fromDate(new Date(Date.now() + 365 * 24 * 60 * 60 * 1000));

      // Prepare update data with all form fields
      const updateData: any = {
        'basic.title': formData.listingHeadline || listing.basic.title || 'Untitled Listing',
        'basic.description': formData.generalSummary || listing.basic.description || '',
        'basic.shortDescription': (formData.generalSummary || listing.basic.description || '').substring(0, 200),
        'basic.category': formData.categories[0] || listing.basic.category || 'Other',
        'basic.subcategory': formData.categories[1] || listing.basic.subcategory || '',
        'basic.tags': formData.categories.length > 0 ? formData.categories : listing.basic.tags || [],
        'basic.updatedAt': Timestamp.now(),
        'basic.expiresAt': expiresAt,
        'location.city': formData.cityTown || listing.location?.city || '',
        'location.province': formData.region || listing.location?.province || '',
        'location.region': formData.region || listing.location?.region || '',
        'financial.askingPrice': formData.askingPriceSpecific ? parseFloat(formData.askingPriceSpecific) : (listing.financial?.askingPrice || 0),
        'financial.annualRevenue': formData.salesRevenueSpecific ? parseFloat(formData.salesRevenueSpecific) : (listing.financial?.annualRevenue || 0),
        'financial.annualCashFlow': formData.cashFlowSpecific ? parseFloat(formData.cashFlowSpecific) : (listing.financial?.annualCashFlow || 0),
        'financial.financingAvailable': formData.financingAvailable,
        'media.images': uploadedPhotos.length > 0 ? uploadedPhotos : (listing.media?.images || []),
        'media.documents': uploadedDocuments.length > 0 ? uploadedDocuments : (listing.media?.documents || []),
        'media.featuredImage': uploadedPhotos[0] || listing.media?.featuredImage || '',
        'owner.contactInfo.name': formData.contactName || listing.owner?.contactInfo?.name || '',
        'owner.contactInfo.email': formData.contactEmail || listing.owner?.contactInfo?.email || '',
        'owner.contactInfo.phone': formData.contactPhone || listing.owner?.contactInfo?.phone || '',
        'search.searchableText': `${formData.listingHeadline || ''} ${formData.generalSummary || ''}`,
        'search.locationKeywords': [formData.region || '', formData.cityTown || ''],
        'search.categoryKeywords': formData.categories.length > 0 ? formData.categories : (listing.search?.categoryKeywords || [])
      };

      // Only set status to 'active' and publishedAt if it's a new submission (not editing an active listing)
      if (!isActiveListing) {
        updateData['basic.status'] = 'active';
        updateData['basic.publishedAt'] = Timestamp.now();
      }

      // Parse employees field (format: "min-max" or just a number)
      let employeesMin = 0;
      let employeesMax = 0;
      if (formData.employees) {
        const employeesMatch = formData.employees.match(/(\d+)\s*-\s*(\d+)/);
        if (employeesMatch) {
          employeesMin = parseInt(employeesMatch[1]);
          employeesMax = parseInt(employeesMatch[2]);
        } else {
          const singleNum = parseInt(formData.employees);
          if (!isNaN(singleNum)) {
            employeesMin = singleNum;
            employeesMax = singleNum;
          }
        }
      }

      // Parse years established
      const yearsEstablished = formData.yearsEstablished ? parseInt(formData.yearsEstablished) : 0;

      // Update business fields
      updateData['business.location'] = formData.location || '';
      updateData['business.competition'] = formData.competition || '';
      updateData['business.expansionPotential'] = formData.expansionPotential || '';
      updateData['business.established'] = isNaN(yearsEstablished) ? 0 : yearsEstablished;
      updateData['business.employees.min'] = employeesMin;
      updateData['business.employees.max'] = employeesMax;
      updateData['business.tradingHours'] = formData.tradingHours || '';
      updateData['business.supportTraining'] = formData.supportTraining || '';
      updateData['business.relocatable'] = formData.relocatable === 'yes';
      updateData['business.workFromHome'] = formData.workFromHome;
      updateData['business.franchise'] = formData.franchise;
      updateData['business.assetSale'] = formData.assetSale;
      updateData['business.leaseTerms'] = formData.leaseTerms || '';
      updateData['business.reasonForSale'] = formData.reasonForSale || '';
      updateData['websiteAddress'] = formData.websiteAddress || '';

      // Update price range based on asking price
      const askingPrice = formData.askingPriceSpecific ? parseFloat(formData.askingPriceSpecific) : (listing.financial?.askingPrice || 0);
      let priceRange = 'under_100k';
      if (askingPrice >= 100000 && askingPrice < 500000) priceRange = '100k_500k';
      else if (askingPrice >= 500000 && askingPrice < 1000000) priceRange = '500k_1m';
      else if (askingPrice >= 1000000 && askingPrice < 5000000) priceRange = '1m_5m';
      else if (askingPrice >= 5000000) priceRange = 'over_5m';
      updateData['search.priceRange'] = priceRange;
      updateData['search.revenueRange'] = priceRange;

      // Update the listing
      const docRef = doc(db, 'businesses', savedListingId);
      await updateDoc(docRef, updateData);

      // Sync franchise collection when franchise flag changes
      try {
        if (formData.franchise) {
          await upsertFranchiseListing('businesses', savedListingId, {
            title: formData.listingHeadline || listing.basic.title || 'Untitled Listing',
            description: formData.generalSummary || listing.basic.description || '',
            category: formData.categories[0] || listing.basic.category || 'Other',
            subcategory: formData.categories[1] || listing.basic.subcategory || '',
            province: formData.region || listing.location?.province || '',
            city: formData.cityTown || listing.location?.city || '',
            askingPrice: formData.askingPriceSpecific ? parseFloat(formData.askingPriceSpecific) : (listing.financial?.askingPrice || 0),
            currency: (listing.financial?.currency as string) || 'CAD',
            annualRevenue: formData.salesRevenueSpecific ? parseFloat(formData.salesRevenueSpecific) : (listing.financial?.annualRevenue || 0),
            annualCashFlow: formData.cashFlowSpecific ? parseFloat(formData.cashFlowSpecific) : (listing.financial?.annualCashFlow || 0),
            featuredImage: uploadedPhotos[0] || listing.media?.featuredImage || listing.media?.images?.[0] || '',
            status: !isActiveListing ? 'active' : (listing.basic.status || 'active'),
            createdAt: listing.basic.createdAt || Timestamp.now(),
            updatedAt: Timestamp.now(),
            tags: formData.categories.length > 0 ? formData.categories : listing.basic.tags,
            seoUrl: listing.basic.seoUrl,
          });
        } else {
          await removeFranchiseListing('businesses', savedListingId);
        }
      } catch (franchiseErr) {
        console.warn('Franchise collection sync failed (non-blocking):', franchiseErr);
      }

      // Navigate to success page with listing ID (only for new submissions, not edits)
      if (!isActiveListing) {
        navigate(`/listing-submitted?listing=${savedListingId}`);
      } else {
        // For edits, go back to dashboard
        navigate('/account/dashboard');
      }
    } catch (error: any) {
      console.error('Error submitting listing:', error);
      alert('Error submitting listing: ' + (error.message || 'Please try again'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const saveForLater = async () => {
    if (!savedListingId) {
      // If no saved listing, just save to localStorage as before
      localStorage.setItem('sellBusinessForm', JSON.stringify(formData));
      alert('Form saved for later!');
      return;
    }

    if (!currentUser) {
      alert('Please sign in to save your draft.');
      return;
    }

    try {
      setIsSaving(true);

      // Get the current listing to preserve package and payment info
      const listing = await businessService.getBusiness(savedListingId);
      if (!listing || listing.owner.userId !== currentUser.uid) {
        alert('Error: Listing not found or you do not have permission to edit it.');
        setIsSaving(false);
        return;
      }

      // Prepare update data with all form fields
      const updateData: any = {
        'basic.title': formData.listingHeadline || listing.basic.title || 'Draft Listing',
        'basic.description': formData.generalSummary || listing.basic.description || '',
        'basic.shortDescription': (formData.generalSummary || listing.basic.description || '').substring(0, 200),
        'basic.category': formData.categories[0] || listing.basic.category || 'Other',
        'basic.subcategory': formData.categories[1] || listing.basic.subcategory || '',
        'basic.tags': formData.categories.length > 0 ? formData.categories : listing.basic.tags || [],
        'basic.updatedAt': Timestamp.now(),
        'location.city': formData.cityTown || listing.location?.city || '',
        'location.province': formData.region || listing.location?.province || '',
        'location.region': formData.region || listing.location?.region || '',
        'financial.askingPrice': formData.askingPriceSpecific ? parseFloat(formData.askingPriceSpecific) : (listing.financial?.askingPrice || 0),
        'financial.annualRevenue': formData.salesRevenueSpecific ? parseFloat(formData.salesRevenueSpecific) : (listing.financial?.annualRevenue || 0),
        'financial.annualCashFlow': formData.cashFlowSpecific ? parseFloat(formData.cashFlowSpecific) : (listing.financial?.annualCashFlow || 0),
        'financial.financingAvailable': formData.financingAvailable,
        'media.images': uploadedPhotos.length > 0 ? uploadedPhotos : (listing.media?.images || []),
        'media.documents': uploadedDocuments.length > 0 ? uploadedDocuments : (listing.media?.documents || []),
        'media.featuredImage': uploadedPhotos[0] || listing.media?.featuredImage || '',
        'owner.contactInfo.name': formData.contactName || listing.owner?.contactInfo?.name || '',
        'owner.contactInfo.email': formData.contactEmail || listing.owner?.contactInfo?.email || '',
        'owner.contactInfo.phone': formData.contactPhone || listing.owner?.contactInfo?.phone || '',
        'search.searchableText': `${formData.listingHeadline || ''} ${formData.generalSummary || ''}`,
        'search.locationKeywords': [formData.region || '', formData.cityTown || ''],
        'search.categoryKeywords': formData.categories.length > 0 ? formData.categories : (listing.search?.categoryKeywords || [])
      };

      // Parse employees field (format: "min-max" or just a number)
      let employeesMin = 0;
      let employeesMax = 0;
      if (formData.employees) {
        const employeesMatch = formData.employees.match(/(\d+)\s*-\s*(\d+)/);
        if (employeesMatch) {
          employeesMin = parseInt(employeesMatch[1]);
          employeesMax = parseInt(employeesMatch[2]);
        } else {
          const singleNum = parseInt(formData.employees);
          if (!isNaN(singleNum)) {
            employeesMin = singleNum;
            employeesMax = singleNum;
          }
        }
      }

      // Parse years established
      const yearsEstablished = formData.yearsEstablished ? parseInt(formData.yearsEstablished) : 0;

      // Update business fields
      updateData['business.location'] = formData.location || '';
      updateData['business.competition'] = formData.competition || '';
      updateData['business.expansionPotential'] = formData.expansionPotential || '';
      updateData['business.established'] = isNaN(yearsEstablished) ? 0 : yearsEstablished;
      updateData['business.employees.min'] = employeesMin;
      updateData['business.employees.max'] = employeesMax;
      updateData['business.tradingHours'] = formData.tradingHours || '';
      updateData['business.supportTraining'] = formData.supportTraining || '';
      updateData['business.relocatable'] = formData.relocatable === 'yes';
      updateData['business.workFromHome'] = formData.workFromHome;
      updateData['business.franchise'] = formData.franchise;
      updateData['business.assetSale'] = formData.assetSale;
      updateData['business.leaseTerms'] = formData.leaseTerms || '';
      updateData['business.reasonForSale'] = formData.reasonForSale || '';
      updateData['websiteAddress'] = formData.websiteAddress || '';

      // Update price range based on asking price
      const askingPrice = formData.askingPriceSpecific ? parseFloat(formData.askingPriceSpecific) : (listing.financial?.askingPrice || 0);
      let priceRange = 'under_100k';
      if (askingPrice >= 100000 && askingPrice < 500000) priceRange = '100k_500k';
      else if (askingPrice >= 500000 && askingPrice < 1000000) priceRange = '500k_1m';
      else if (askingPrice >= 1000000 && askingPrice < 5000000) priceRange = '1m_5m';
      else if (askingPrice >= 5000000) priceRange = 'over_5m';
      updateData['search.priceRange'] = priceRange;
      updateData['search.revenueRange'] = priceRange;

      // Update the listing
      const docRef = doc(db, 'businesses', savedListingId);
      await updateDoc(docRef, updateData);

      // Sync franchise collection when franchise flag changes (draft stays in collection with status 'draft')
      try {
        if (formData.franchise) {
          const now = Timestamp.now();
          await upsertFranchiseListing('businesses', savedListingId, {
            title: formData.listingHeadline || 'Draft Listing',
            description: formData.generalSummary || '',
            category: formData.categories[0] || 'Other',
            subcategory: formData.categories[1] || '',
            province: formData.region || '',
            city: formData.cityTown || '',
            askingPrice: formData.askingPriceSpecific ? parseFloat(formData.askingPriceSpecific) : 0,
            currency: 'CAD',
            annualRevenue: formData.salesRevenueSpecific ? parseFloat(formData.salesRevenueSpecific) : 0,
            annualCashFlow: formData.cashFlowSpecific ? parseFloat(formData.cashFlowSpecific) : 0,
            featuredImage: uploadedPhotos[0] || '',
            status: 'draft',
            createdAt: now,
            updatedAt: now,
            tags: formData.categories,
          });
        } else {
          await removeFranchiseListing('businesses', savedListingId);
        }
      } catch (franchiseErr) {
        console.warn('Franchise collection sync failed (non-blocking):', franchiseErr);
      }

      // Update URL to include draft ID so it persists on refresh
      if (!searchParams.get('draft')) {
        const newUrl = window.location.pathname + '?draft=' + savedListingId;
        window.history.replaceState({}, document.title, newUrl);
      }
    } catch (error: any) {
      console.error('Error saving draft:', error);
      alert('Error saving draft: ' + (error.message || 'Please try again'));
    } finally {
      setIsSaving(false);
    }
  };

  const [selectedPackage, setSelectedPackage] = useState<string>('6months');

  // Update selected package based on province
  useEffect(() => {
    const isOntarioOrAlberta = formData.region === 'Ontario' || formData.region === 'Alberta';
    if (isOntarioOrAlberta) {
      setSelectedPackage('6months');
    } else {
      setSelectedPackage('6months');
    }
  }, [formData.region]);

  const renderChoosePackage = () => {
    // Check if the selected province is Ontario or Alberta
    const isOntarioOrAlberta = formData.region === 'Ontario' || formData.region === 'Alberta';
    
    // Helper function to get price based on currency
    const getPrice = (packageId: string) => {
      const pkg = PACKAGES[packageId];
      if (!pkg) return 0;
      return selectedCurrency === 'USD' ? pkg.priceUSD : pkg.priceCAD;
    };

    const getCurrencySymbol = () => selectedCurrency === 'USD' ? '$' : 'C$';
    
    return (
    <div className="space-y-10">
      {/* Introduction */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-3">
          <h2 className="text-3xl font-bold text-gray-900">Choose your listing package</h2>
          {/* Currency Switcher */}
          <div className="inline-flex rounded-lg border-2 border-gray-300 bg-white p-1">
            <button
              onClick={() => setSelectedCurrency('CAD')}
              className={`px-4 py-2 rounded-md text-sm font-semibold transition-all ${
                selectedCurrency === 'CAD'
                  ? 'bg-primary-600 text-white shadow-md'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              CAD
            </button>
            <button
              onClick={() => setSelectedCurrency('USD')}
              className={`px-4 py-2 rounded-md text-sm font-semibold transition-all ${
                selectedCurrency === 'USD'
                  ? 'bg-primary-600 text-white shadow-md'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              USD
            </button>
          </div>
        </div>
        <p className="text-lg text-gray-700">
          Select the option that best fits your business sale needs and start reaching qualified buyers today.
        </p>
      </div>

      {/* Package Selection - Vertical Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
        {/* 3-Months Plan - Always shown */}
        <div className={`rounded-2xl transition-all duration-300 transform hover:scale-[1.02] ${
          selectedPackage === '3months' 
            ? 'bg-gradient-to-r from-primary-600 via-primary-700 to-secondary-600 p-[2px] shadow-xl' 
            : 'bg-white border-2 border-gray-200 hover:border-gray-300 shadow-md hover:shadow-lg'
        }`}>
          <div className={`${selectedPackage === '3months' ? 'bg-gray-50 rounded-2xl h-full' : ''} p-8`}>
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">3-Months</h3>
              <div className="text-4xl font-bold text-gray-900 mb-3">{getCurrencySymbol()}{getPrice('3months')}</div>
              <p className="text-base text-gray-600 font-medium">One-time payment</p>
            </div>
            
            <div className="space-y-6 mb-8">
              {/* Listing Section */}
              <div>
                <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3">Includes:</h4>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <span className="text-green-500 mr-3 text-lg font-bold">✓</span>
                    <span className="text-base text-gray-700 leading-relaxed font-bold">3-month listing duration</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-3 text-lg font-bold">✓</span>
                    <span className="text-base text-gray-700 leading-relaxed flex items-center gap-2 font-bold">
                      <img 
                        src="/assets/images/realtor-ca.png" 
                        alt="REALTOR.ca" 
                        className="h-[1.9rem] object-contain"
                      />
                      listing included
                    </span>
                  </li>
                  {/* <li className="flex items-start">
                    <span className="text-green-500 mr-3 text-lg font-bold">✓</span>
                    <span className="text-base text-gray-700 leading-relaxed">Renewed at $59 per month, until cancelled</span>
                  </li> */}
                </ul>
              </div>

              {/* Support Section */}
              <div>
                <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3">Support</h4>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <span className="text-green-500 mr-3 text-lg font-bold">✓</span>
                    <span className="text-base text-gray-700 leading-relaxed">Instant lead notifications</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-3 text-lg font-bold">✓</span>
                    <span className="text-base text-gray-700 leading-relaxed">Expert email support</span>
                  </li>
                </ul>
              </div>
            </div>
            
            <button 
              onClick={() => setSelectedPackage('3months')}
              className={`w-full py-4 px-6 rounded-lg font-semibold text-base transition-all duration-300 ${
                selectedPackage === '3months'
                  ? 'bg-gradient-to-r from-primary-600 via-primary-700 to-secondary-600 hover:from-primary-700 hover:via-primary-800 hover:to-secondary-700 text-white shadow-lg'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-900 border-2 border-gray-300'
              }`}
            >
              {selectedPackage === '3months' ? (
                <span className="flex items-center justify-center">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Selected
                </span>
              ) : (
                'Select This Package'
              )}
            </button>
          </div>
        </div>

        {/* Conditional plans based on province */}
        {isOntarioOrAlberta ? (
          <>
            {/* 6-Months Plan for Ontario/Alberta */}
            <div className={`rounded-2xl relative transition-all duration-300 transform hover:scale-[1.02] ${
              selectedPackage === '6months' 
                ? 'bg-gradient-to-r from-primary-600 via-primary-700 to-secondary-600 p-[2px] shadow-xl' 
                : 'bg-white border-2 border-gray-200 hover:border-gray-300 shadow-md hover:shadow-lg'
            }`}>
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                <span className="bg-gradient-to-r from-primary-600 via-primary-700 to-secondary-600 text-white px-5 py-2 rounded-full text-sm font-semibold shadow-md">MOST POPULAR</span>
              </div>
              <div className={`${selectedPackage === '6months' ? 'bg-gray-50 rounded-2xl h-full' : ''} p-8 pt-12`}>
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">6-Months</h3>
                <div className="text-4xl font-bold text-gray-900 mb-3">{getCurrencySymbol()}{getPrice('6months')}</div>
                <p className="text-base text-gray-600 font-medium">One-time payment</p>
              </div>
                
                <div className="space-y-6 mb-8">
                  {/* Listing Section */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3">Includes:</h4>
                    <ul className="space-y-3">
                      <li className="flex items-start">
                        <span className="text-green-500 mr-3 text-lg font-bold">✓</span>
                        <span className="text-base text-gray-700 leading-relaxed font-bold">6-month listing duration</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-500 mr-3 text-lg font-bold">✓</span>
                        <span className="text-base text-gray-700 leading-relaxed flex items-center gap-2 font-bold">
                          <img 
                            src="/assets/images/realtor-ca.png" 
                            alt="REALTOR.ca" 
                            className="h-[1.9rem] object-contain"
                          />
                          listing
                        </span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-500 mr-3 text-lg font-bold">✓</span>
                        <span className="text-base text-gray-700 leading-relaxed">Renewed at $49 per month, until cancelled</span>
                      </li>
                    </ul>
                  </div>

                  {/* Support Section */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3">Support</h4>
                    <ul className="space-y-3">
                      <li className="flex items-start">
                        <span className="text-green-500 mr-3 text-lg font-bold">✓</span>
                        <span className="text-base text-gray-700 leading-relaxed">Instant lead notifications</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-500 mr-3 text-lg font-bold">✓</span>
                        <span className="text-base text-gray-700 leading-relaxed">Expert email support</span>
                      </li>
                    </ul>
                  </div>

                  {/* Bonus Section */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3">Bonus</h4>
                    <ul className="space-y-3">
                      <li className="flex items-start">
                        <span className="text-green-500 mr-3 text-lg font-bold">✓</span>
                        <span className="text-base text-gray-700 leading-relaxed">Featured listing placement</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-500 mr-3 text-lg font-bold">✓</span>
                        <span className="text-base text-gray-700 leading-relaxed">Email blast to 6,300+ subscribers</span>
                      </li>
                    </ul>
                  </div>
                </div>
                
                <button 
                  onClick={() => setSelectedPackage('6months')}
                  className={`w-full py-4 px-6 rounded-lg font-semibold text-base transition-all duration-300 ${
                    selectedPackage === '6months'
                      ? 'bg-gradient-to-r from-primary-600 via-primary-700 to-secondary-600 hover:from-primary-700 hover:via-primary-800 hover:to-secondary-700 text-white shadow-lg'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-900 border-2 border-gray-300'
                  }`}
                >
                  {selectedPackage === '6months' ? (
                    <span className="flex items-center justify-center">
                      <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Selected
                    </span>
                  ) : (
                    'Select This Package'
                  )}
                </button>
              </div>
            </div>

            {/* 12-Months Plan for Ontario/Alberta */}
            <div className={`rounded-2xl transition-all duration-300 transform hover:scale-[1.02] ${
              selectedPackage === '12months' 
                ? 'bg-gradient-to-r from-primary-600 via-primary-700 to-secondary-600 p-[2px] shadow-xl' 
                : 'bg-white border-2 border-gray-200 hover:border-gray-300 shadow-md hover:shadow-lg'
            }`}>
              <div className={`${selectedPackage === '12months' ? 'bg-gray-50 rounded-2xl h-full' : ''} p-8`}>
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">12-Months</h3>
                  <div className="text-4xl font-bold text-gray-900 mb-3">{getCurrencySymbol()}{getPrice('12months')}</div>
                  <p className="text-base text-gray-600 font-medium">Until sold</p>
                </div>
                
                <div className="space-y-6 mb-8">
                  {/* Listing Section */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3">Includes:</h4>
                    <ul className="space-y-3">
                      <li className="flex items-start">
                        <span className="text-green-500 mr-3 text-lg font-bold">✓</span>
                        <span className="text-base text-gray-700 leading-relaxed font-bold">6-month listing duration</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-500 mr-3 text-lg font-bold">✓</span>
                        <span className="text-base text-gray-700 leading-relaxed flex items-center gap-2 font-bold">
                          <img 
                            src="/assets/images/realtor-ca.png" 
                            alt="REALTOR.ca" 
                            className="h-[1.9rem] object-contain"
                          />
                          listing
                        </span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-500 mr-3 text-lg font-bold">✓</span>
                        <span className="text-base text-gray-700 leading-relaxed font-bold">Featured listing placement</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-500 mr-3 text-lg font-bold">✓</span>
                        <span className="text-base text-gray-700 leading-relaxed font-bold">Email blast to subscribers</span>
                      </li>
                    </ul>
                  </div>

                  {/* Support Section */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3">Support</h4>
                    <ul className="space-y-3">
                      <li className="flex items-start">
                        <span className="text-green-500 mr-3 text-lg font-bold">✓</span>
                        <span className="text-base text-gray-700 leading-relaxed">Instant lead notifications</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-500 mr-3 text-lg font-bold">✓</span>
                        <span className="text-base text-gray-700 leading-relaxed">Expert email support</span>
                      </li>
                    </ul>
                  </div>
                </div>
                
                <button 
                  onClick={() => setSelectedPackage('12months')}
                  className={`w-full py-4 px-6 rounded-lg font-semibold text-base transition-all duration-300 ${
                    selectedPackage === '12months'
                      ? 'bg-gradient-to-r from-primary-600 via-primary-700 to-secondary-600 hover:from-primary-700 hover:via-primary-800 hover:to-secondary-700 text-white shadow-lg'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-900 border-2 border-gray-300'
                  }`}
                >
                  {selectedPackage === '12months' ? (
                    <span className="flex items-center justify-center">
                      <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Selected
                    </span>
                  ) : (
                    'Select This Package'
                  )}
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* 6-Months Plan for other provinces */}
            <div className={`rounded-2xl relative transition-all duration-300 transform hover:scale-[1.02] ${
              selectedPackage === '6months' 
                ? 'bg-gradient-to-r from-primary-600 via-primary-700 to-secondary-600 p-[2px] shadow-xl' 
                : 'bg-white border-2 border-gray-200 hover:border-gray-300 shadow-md hover:shadow-lg'
            }`}>
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                <span className="bg-gradient-to-r from-primary-600 via-primary-700 to-secondary-600 text-white px-5 py-2 rounded-full text-sm font-semibold shadow-md">MOST POPULAR</span>
              </div>
              <div className={`${selectedPackage === '6months' ? 'bg-gray-50 rounded-2xl h-full' : ''} p-8 pt-12`}>
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">6-Months</h3>
                  <div className="text-4xl font-bold text-gray-900 mb-3">{getCurrencySymbol()}{getPrice('6months')}</div>
                  <p className="text-base text-gray-600 font-medium">One-time payment</p>
                </div>
                
                <div className="space-y-6 mb-8">
                  {/* Listing Section */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3">Listing</h4>
                    <ul className="space-y-3">
                      <li className="flex items-start">
                        <span className="text-green-500 mr-3 text-lg font-bold">✓</span>
                        <span className="text-base text-gray-700 leading-relaxed font-bold">6-month listing duration</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-500 mr-3 text-lg font-bold">✓</span>
                        <span className="text-base text-gray-700 leading-relaxed flex items-center gap-2 font-bold">
                          <img 
                            src="/assets/images/realtor-ca.png" 
                            alt="REALTOR.ca" 
                            className="h-[1.9rem] object-contain"
                          />
                          listing included
                        </span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-500 mr-3 text-lg font-bold">✓</span>
                        <span className="text-base text-gray-700 leading-relaxed font-bold">Featured listing placement</span>
                      </li>
                    </ul>
                  </div>

                  {/* Support Section */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3">Support</h4>
                    <ul className="space-y-3">
                      <li className="flex items-start">
                        <span className="text-green-500 mr-3 text-lg font-bold">✓</span>
                        <span className="text-base text-gray-700 leading-relaxed">Instant lead notifications</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-500 mr-3 text-lg font-bold">✓</span>
                        <span className="text-base text-gray-700 leading-relaxed">Expert email support</span>
                      </li>
                    </ul>
                  </div>
                </div>
                
                <button 
                  onClick={() => setSelectedPackage('6months')}
                  className={`w-full py-4 px-6 rounded-lg font-semibold text-base transition-all duration-300 ${
                    selectedPackage === '6months'
                      ? 'bg-gradient-to-r from-primary-600 via-primary-700 to-secondary-600 hover:from-primary-700 hover:via-primary-800 hover:to-secondary-700 text-white shadow-lg'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-900 border-2 border-gray-300'
                  }`}
                >
                  {selectedPackage === '6months' ? (
                    <span className="flex items-center justify-center">
                      <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Selected
                    </span>
                  ) : (
                    'Select This Package'
                  )}
                </button>
              </div>
            </div>

            {/* 12-Months Plan for other provinces */}
            <div className={`rounded-2xl transition-all duration-300 transform hover:scale-[1.02] ${
              selectedPackage === '12months' 
                ? 'bg-gradient-to-r from-primary-600 via-primary-700 to-secondary-600 p-[2px] shadow-xl' 
                : 'bg-white border-2 border-gray-200 hover:border-gray-300 shadow-md hover:shadow-lg'
            }`}>
              <div className={`${selectedPackage === '12months' ? 'bg-gray-50 rounded-2xl h-full' : ''} p-8`}>
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">12-Months</h3>
                  <div className="text-4xl font-bold text-gray-900 mb-3">{getCurrencySymbol()}{getPrice('12months')}</div>
                  <p className="text-base text-gray-600 font-medium">One-time payment</p>
                </div>
                
                <div className="space-y-6 mb-8">
                  {/* Listing Section */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3">Listing</h4>
                    <ul className="space-y-3">
                      <li className="flex items-start">
                        <span className="text-green-500 mr-3 text-lg font-bold">✓</span>
                        <span className="text-base text-gray-700 leading-relaxed font-bold">12-month listing duration</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-500 mr-3 text-lg font-bold">✓</span>
                        <span className="text-base text-gray-700 leading-relaxed flex items-center gap-2 font-bold">
                          <img 
                            src="/assets/images/realtor-ca.png" 
                            alt="REALTOR.ca" 
                            className="h-[1.9rem] object-contain"
                          />
                          listing included
                        </span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-500 mr-3 text-lg font-bold">✓</span>
                        <span className="text-base text-gray-700 leading-relaxed font-bold">Featured listing placement</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-500 mr-3 text-lg font-bold">✓</span>
                        <span className="text-base text-gray-700 leading-relaxed font-bold">Email blast to subscribers</span>
                      </li>
                    </ul>
                  </div>

                  {/* Support Section */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3">Support</h4>
                    <ul className="space-y-3">
                      <li className="flex items-start">
                        <span className="text-green-500 mr-3 text-lg font-bold">✓</span>
                        <span className="text-base text-gray-700 leading-relaxed">Instant lead notifications</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-500 mr-3 text-lg font-bold">✓</span>
                        <span className="text-base text-gray-700 leading-relaxed">Expert email support</span>
                      </li>
                    </ul>
                  </div>
                </div>
                
                <button 
                  onClick={() => setSelectedPackage('12months')}
                  className={`w-full py-4 px-6 rounded-lg font-semibold text-base transition-all duration-300 ${
                    selectedPackage === '12months'
                      ? 'bg-gradient-to-r from-primary-600 via-primary-700 to-secondary-600 hover:from-primary-700 hover:via-primary-800 hover:to-secondary-700 text-white shadow-lg'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-900 border-2 border-gray-300'
                  }`}
                >
                  {selectedPackage === '12months' ? (
                    <span className="flex items-center justify-center">
                      <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Selected
                    </span>
                  ) : (
                    'Select This Package'
                  )}
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Action Buttons - Removed from pricing section since it's now on step 1 */}
      {/* Trust Indicators */}
      {/* <div className="mt-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-center">
          <div className="flex flex-col items-center">
            <h4 className="font-bold text-gray-900 mb-2">Secure Payment</h4>
            <p className="text-sm text-gray-600">SSL encrypted transactions</p>
          </div>
          <div className="flex flex-col items-center">
            <h4 className="font-bold text-gray-900 mb-2">24/7 Support</h4>
            <p className="text-sm text-gray-600">Expert assistance available</p>
          </div>
        </div>
      </div> */}
    </div>
  );
  };

  const renderStep1 = () => (
    <div className="space-y-8">
      {/* Pricing Section - Moved from Step 2 */}
      {renderChoosePackage()}

      {/* Introduction */}
      <div className="bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 p-8 rounded-2xl border border-blue-100 shadow-lg">
        <div className="flex items-start space-x-4">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
            <span className="text-white text-2xl">💡</span>
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-3">Getting Started</h3>
            <p className="text-gray-700 text-sm leading-relaxed">
              Unless stated as required (*), all fields are optional. We understand that anonymity and privacy may sometimes be key considerations when selling a business - therefore business names and location details do not have to be shown. To achieve the best results please ensure you fill out as much information as possible. Please note: Do not use all capital letters.
            </p>
          </div>
        </div>
      </div>

      {/* Listing Details */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-200/50 p-8 backdrop-blur-sm">
        <div className="flex items-center mb-6">
          <div className="w-2 h-8 bg-gradient-to-b from-primary-500 to-secondary-500 rounded-full mr-4"></div>
          <h3 className="text-xl font-bold text-gray-900">Listing Details</h3>
        </div>
        
        <div className="space-y-8">
          {/* Listing Headline */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Listing Headline *
            </label>
            <input
              type="text"
              value={formData.listingHeadline}
              onChange={(e) => handleInputChange('listingHeadline', e.target.value)}
              className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-300 shadow-sm hover:shadow-md ${
                !formData.listingHeadline.trim() ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
              }`}
              placeholder="e.g., Profitable and Established Coffee Shop in Devon"
            />
            {!formData.listingHeadline.trim() && (
              <p className="text-xs text-red-600 mt-1">Listing headline is required</p>
            )}
            <p className="text-xs text-gray-600 mt-2">
              This will be the title of your listing, and it is limited to 8 words. For example: Profitable and Established Coffee Shop in Devon. We'll automatically add 'For Sale' to the end of your title, but this is not included in the word count.
            </p>
          </div>

          {/* Is this business a Franchise? */}
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="listing-franchise"
                type="checkbox"
                checked={formData.franchise}
                onChange={(e) => handleInputChange('franchise', e.target.checked)}
                className="w-5 h-5 text-primary-600 border-gray-300 rounded focus:ring-primary-500 focus:ring-2 cursor-pointer"
              />
            </div>
            <div className="ml-3">
              <label htmlFor="listing-franchise" className="text-sm font-semibold text-gray-700 cursor-pointer">
                Is the business being listed a Franchise?
              </label>
              <p className="text-xs text-gray-600 mt-1">
                Check this box if the business is a franchise.
              </p>
            </div>
          </div>

        </div>
      </div>

      {/* Contact Information */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-200/50 p-8 backdrop-blur-sm">
        <div className="flex items-center mb-6">
          <div className="w-2 h-8 bg-gradient-to-b from-green-500 to-emerald-600 rounded-full mr-4"></div>
          <h3 className="text-xl font-bold text-gray-900">Contact Information</h3>
        </div>
        
        <div className="space-y-6">
          {/* Radio Buttons - Only show when not logged in */}
          {!currentUser && (
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
              <div className="flex flex-col gap-4">
                <label className="flex items-center cursor-pointer group">
                  <input
                    type="radio"
                    name="contactOption"
                    value="login"
                    checked={contactOption === 'login'}
                    onChange={(e) => setContactOption(e.target.value as 'login' | 'create_account')}
                    className="w-5 h-5 text-primary-600 focus:ring-primary-500 focus:ring-2 border-gray-300 cursor-pointer"
                  />
                  <span className="ml-3 text-base font-semibold text-gray-700 group-hover:text-primary-600">
                    Already a member?
                  </span>
                </label>
                <label className="flex items-center cursor-pointer group">
                  <input
                    type="radio"
                    name="contactOption"
                    value="create_account"
                    checked={contactOption === 'create_account'}
                    onChange={(e) => setContactOption(e.target.value as 'login' | 'create_account')}
                    className="w-5 h-5 text-primary-600 focus:ring-primary-500 focus:ring-2 border-gray-300 cursor-pointer"
                  />
                  <span className="ml-3 text-base font-semibold text-gray-700 group-hover:text-primary-600">
                    Create account, it's free!
                  </span>
                </label>
              </div>
            </div>
          )}

          {/* Sign In Form - Show when "login" is selected and not logged in */}
          {!currentUser && contactOption === 'login' && (
            <div className="bg-gradient-to-r from-primary-50 via-primary-100 to-secondary-50 border-2 border-primary-300 rounded-xl p-6 shadow-md">
              <form onSubmit={handleSignIn} className="space-y-4">
                <div className="flex items-center mb-4">
                  <div className="w-2 h-6 bg-gradient-to-b from-primary-500 to-secondary-600 rounded-full mr-3"></div>
                  <h4 className="text-lg font-bold text-gray-900">Sign In</h4>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Sign In Email */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={signInEmail}
                      onChange={(e) => setSignInEmail(e.target.value)}
                      className="w-full px-4 py-3 border border-primary-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-300 shadow-sm hover:shadow-md bg-white"
                      placeholder="your.email@example.com"
                    />
                  </div>

                  {/* Sign In Password */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Password
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="password"
                        value={signInPassword}
                        onChange={(e) => setSignInPassword(e.target.value)}
                        className="flex-1 px-4 py-3 border border-primary-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-300 shadow-sm hover:shadow-md bg-white"
                        placeholder="Enter your password"
                      />
                      <button
                        type="submit"
                        disabled={isSigningIn || !signInEmail.trim() || !signInPassword.trim()}
                        className="px-6 py-3 bg-gradient-to-r from-primary-600 via-primary-700 to-secondary-600 hover:from-primary-700 hover:via-primary-800 hover:to-secondary-700 text-white font-semibold rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                      >
                        {isSigningIn ? 'Signing In...' : 'Sign In'}
                      </button>
                    </div>
                  </div>
                </div>
                {signInError && (
                  <p className="text-sm text-red-600 mt-2">{signInError}</p>
                )}
              </form>
            </div>
          )}

          {/* Registration Form - Show when "create_account" is selected or when logged in */}
          {(!currentUser && contactOption === 'create_account') || currentUser ? (
            <div className={!currentUser && contactOption === 'create_account' ? "bg-gradient-to-r from-primary-50 via-primary-100 to-secondary-50 border-2 border-primary-300 rounded-xl p-6 shadow-md" : ""}>
              {!currentUser && contactOption === 'create_account' && (
                <div className="flex items-center mb-4">
                  <div className="w-2 h-6 bg-gradient-to-b from-primary-500 to-secondary-600 rounded-full mr-3"></div>
                  <h4 className="text-lg font-bold text-gray-900">Create Account</h4>
                </div>
              )}
              <div className="space-y-6">
                {/* Row 1: Name and Email */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Full Name *
                </label>
                <input
                  type="text"
                  value={formData.contactName}
                  onChange={(e) => handleInputChange('contactName', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-300 shadow-sm hover:shadow-md ${
                    !formData.contactName.trim() ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter your full name"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Email Address *
                </label>
                <input
                  type="email"
                  value={formData.contactEmail}
                  onChange={(e) => {
                    if (!currentUser && contactOption === 'create_account') {
                      handleInputChange('contactEmail', e.target.value);
                      if (emailError) {
                        setEmailError('');
                      }
                    }
                  }}
                  onBlur={() => {
                    if (!currentUser && contactOption === 'create_account' && formData.contactEmail) {
                      checkEmailExists(formData.contactEmail);
                    }
                  }}
                  disabled={!!currentUser || (!currentUser && contactOption === 'login')}
                  className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-300 shadow-sm hover:shadow-md ${
                    currentUser || (!currentUser && contactOption === 'login')
                      ? 'bg-gray-100 text-gray-500 cursor-not-allowed border-gray-300' 
                      : (!formData.contactEmail.trim() || emailError ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300')
                  }`}
                  placeholder="your.email@example.com"
                />
                {!currentUser && contactOption === 'create_account' && isCheckingEmail && (
                  <p className="text-xs text-gray-600 mt-1">Checking email...</p>
                )}
                {!currentUser && contactOption === 'create_account' && emailError && (
                  <p className="text-xs text-red-600 mt-1">{emailError}</p>
                )}
              </div>
            </div>

            {/* Row 2: Confirm Email (if creating account) and Phone */}
            <div className={`grid grid-cols-1 ${!currentUser && contactOption === 'create_account' ? 'md:grid-cols-2' : 'md:grid-cols-1'} gap-6`}>
              {/* Confirm Email - Only for non-registered users creating account */}
              {!currentUser && contactOption === 'create_account' && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Confirm Email Address *
                  </label>
                  <input
                    type="email"
                    value={formData.confirmEmail}
                    onChange={(e) => handleInputChange('confirmEmail', e.target.value)}
                    className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-300 shadow-sm hover:shadow-md ${
                      (formData.confirmEmail && formData.contactEmail !== formData.confirmEmail) || !formData.confirmEmail.trim()
                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                        : 'border-gray-300'
                    }`}
                    placeholder="Confirm your email address"
                  />
                  {formData.confirmEmail && formData.contactEmail !== formData.confirmEmail && (
                    <p className="text-xs text-red-600 mt-1">Emails do not match</p>
                  )}
                  {!formData.confirmEmail.trim() && formData.contactEmail && (
                    <p className="text-xs text-red-600 mt-1">Please confirm your email</p>
                  )}
                </div>
              )}

              {/* Phone */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  value={formData.contactPhone}
                  onChange={(e) => handleInputChange('contactPhone', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-300 shadow-sm hover:shadow-md ${
                    !formData.contactPhone.trim() ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>

            {/* Row 3: Password fields - Only for non-registered users creating account */}
            {!currentUser && contactOption === 'create_account' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Password */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Password *
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-300 shadow-sm hover:shadow-md ${
                      !formData.password.trim() ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Create a password"
                  />
                  {formData.password && formData.password.length < 6 && (
                    <p className="text-xs text-red-600 mt-1">Password must be at least 6 characters</p>
                  )}
                </div>

                {/* Confirm Password */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Confirm Password *
                  </label>
                  <input
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                    className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-300 shadow-sm hover:shadow-md ${
                      (formData.confirmPassword && formData.password !== formData.confirmPassword) || !formData.confirmPassword.trim()
                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                        : 'border-gray-300'
                    }`}
                    placeholder="Confirm your password"
                  />
                  {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                    <p className="text-xs text-red-600 mt-1">Passwords do not match</p>
                  )}
                  {!formData.confirmPassword.trim() && formData.password && (
                    <p className="text-xs text-red-600 mt-1">Please confirm your password</p>
                  )}
                </div>
              </div>
            )}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* Bottom Action Buttons */}
      <div className="flex justify-between items-center">
        <div></div>
        <button
          onClick={() => {
            // console.log('Continue button clicked!');
            // console.log('Form data:', formData);
            // console.log('Validation:', {
            //   headline: formData.listingHeadline.trim(),
            //   categories: formData.categories.length,
            //   region: formData.region
            // });
            nextStep();
          }}
          disabled={
            isSaving || 
            !formData.listingHeadline.trim() || 
            !selectedPackage ||
            (!currentUser && contactOption === 'create_account' && (
              !formData.contactName.trim() ||
              !formData.contactEmail.trim() ||
              !formData.contactPhone.trim() ||
              !formData.confirmEmail.trim() ||
              formData.contactEmail !== formData.confirmEmail ||
              !formData.password.trim() ||
              formData.password.length < 6 ||
              !formData.confirmPassword.trim() ||
              formData.password !== formData.confirmPassword ||
              !!emailError
            )) ||
            (!currentUser && contactOption === 'login' && (
              !formData.contactName.trim() ||
              !formData.contactEmail.trim() ||
              !formData.contactPhone.trim()
            )) ||
            (currentUser && (
              !formData.contactName.trim() ||
              !formData.contactPhone.trim()
            ))
          }
          className={`flex items-center gap-3 px-8 py-4 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 ${
            isSaving || 
            !formData.listingHeadline.trim() || 
            !selectedPackage ||
            (!currentUser && contactOption === 'create_account' && (
              !formData.contactName.trim() ||
              !formData.contactEmail.trim() ||
              !formData.contactPhone.trim() ||
              !formData.confirmEmail.trim() ||
              formData.contactEmail !== formData.confirmEmail ||
              !formData.password.trim() ||
              formData.password.length < 6 ||
              !formData.confirmPassword.trim() ||
              formData.password !== formData.confirmPassword ||
              !!emailError
            )) ||
            (!currentUser && contactOption === 'login' && (
              !formData.contactName.trim() ||
              !formData.contactEmail.trim() ||
              !formData.contactPhone.trim()
            )) ||
            (currentUser && (
              !formData.contactName.trim() ||
              !formData.contactPhone.trim()
            ))
              ? 'bg-gray-400 cursor-not-allowed text-white' 
              : 'bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 text-white'
          }`}
        >
          {isSaving ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              Saving Draft...
            </>
          ) : (
            <>
              Pay & Start Listing <ArrowRightIcon className="h-5 w-5" />
            </>
          )}
        </button>
      </div>

      {/* Footer Note */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <p className="text-sm text-yellow-800">
          <strong>Please note:</strong> your video, documents and website address will not show on your listing until you buy an advertising package. In addition this will only show on your listing for registered buyers. Buyers will use this as a way to contact you rather than going through BizBuyMax.com, so by asking buyers to register first, we hope that the buyers that do contact you will be serious about buying your business. Any photographs which reveal the name and location of your business will not be displayed until you buy an advertising package.
        </p>
      </div>
    </div>
  );

  const renderStep2 = () => {
    // Prepare form data for AdditionalInformationForm
    const additionalInfoData: AdditionalInformationFormData = {
      generalSummary: formData.generalSummary,
      location: formData.location,
      competition: formData.competition,
      expansionPotential: formData.expansionPotential,
      yearsEstablished: formData.yearsEstablished,
      employees: formData.employees,
      tradingHours: formData.tradingHours,
      supportTraining: formData.supportTraining,
      relocatable: formData.relocatable,
      workFromHome: formData.workFromHome,
      franchise: formData.franchise,
      assetSale: formData.assetSale,
      financingAvailable: formData.financingAvailable,
      leaseTerms: formData.leaseTerms,
      reasonForSale: formData.reasonForSale,
      askingPriceSpecific: formData.askingPriceSpecific,
      salesRevenueSpecific: formData.salesRevenueSpecific,
      cashFlowSpecific: formData.cashFlowSpecific,
      websiteAddress: formData.websiteAddress
    };

    // Section navigation items
    const sections = [
      { id: 'section-title', label: 'Listing Title', showOnlyOnStep3: true },
      { id: 'section-business-type', label: 'Business Type', showOnlyOnStep3: true },
      { id: 'section-region-city', label: 'Region & City', showOnlyOnStep3: true },
      { id: 'section-general-summary', label: 'General Summary' },
      { id: 'section-photographs', label: 'Photographs' },
      { id: 'section-documents', label: 'Documents' },
      { id: 'section-financial', label: 'Financial Details' },
      { id: 'section-environment', label: 'Environment' },
      { id: 'section-operation', label: 'Operation' },
      { id: 'section-terms', label: 'Terms' },
      { id: 'section-website', label: 'Website' },
      { id: 'section-realtor', label: 'REALTOR.ca Listing' }
    ];

    const handleJumpToSection = (sectionId: string) => {
      const element = document.getElementById(sectionId);
      if (element) {
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - 128; // 8rem = 128px
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });

        // Add highlight effect
        element.classList.add('section-highlight');
        
        // Remove highlight after animation completes
        setTimeout(() => {
          element.classList.remove('section-highlight');
        }, 2000);
      }
    };

    // Only show sidebar on step 3
    if (currentStep === 3) {
      return (
        <div className="flex gap-8 items-start">
          {/* Sticky Left Sidebar - padding only applies when sticky (on scroll) */}
          <div className="w-64 flex-shrink-0 sticky top-4">
            <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6">
              {/* Action Buttons */}
              <div className="space-y-3 mb-6">
                <button
                  onClick={submitListing}
                  disabled={isSaving || isSubmitting}
                  className="w-full px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-700 hover:from-green-700 hover:to-emerald-800 text-white rounded-lg font-semibold transition-all duration-300 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Submit Listing
                    </>
                  )}
                </button>
                <button
                  onClick={saveForLater}
                  disabled={isSaving}
                  className="w-full px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-all duration-300 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSaving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                      </svg>
                      Save For Later
                    </>
                  )}
                </button>
              </div>

              {/* Navigation Links */}
              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Jump to Section</h4>
                <nav className="space-y-1">
                  {sections
                    .filter(section => !section.showOnlyOnStep3 || currentStep === 3)
                    .map((section) => (
                      <button
                        key={section.id}
                        onClick={() => handleJumpToSection(section.id)}
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-primary-600 rounded-md transition-colors duration-150 flex items-center gap-2"
                      >
                        {section.id === 'section-realtor' ? (
                          <>
                            <img 
                              src="/assets/images/realtor-ca.png" 
                              alt="REALTOR.ca" 
                              className="h-5 w-auto"
                            />
                            <span>Listing</span>
                          </>
                        ) : (
                          section.label
                        )}
                      </button>
                    ))}
                </nav>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            <div className="space-y-6">
              {/* Title Field - Only on Step 3 */}
              <div id="section-title" className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <label className="block text-sm font-semibold text-gray-900 mb-3">
                  Listing Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.listingHeadline}
                  onChange={(e) => handleInputChange('listingHeadline', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 ${
                    !formData.listingHeadline.trim() ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter your business listing title"
                />
                {!formData.listingHeadline.trim() && (
                  <p className="text-xs text-red-600 mt-2">Listing title is required</p>
                )}
              </div>

              {/* Select Your Business Type */}
              <div id="section-business-type" className="bg-white rounded-2xl shadow-xl border border-gray-200/50 p-8 backdrop-blur-sm">
                <div className="flex items-center mb-6">
                  <div className="w-2 h-8 bg-gradient-to-b from-blue-500 to-indigo-600 rounded-full mr-4"></div>
                  <h3 className="text-xl font-bold text-gray-900">Select Your Business Type</h3>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Categories *
                  </label>
                  <div className={`border-2 rounded-xl p-4 min-h-[44px] bg-gray-50/50 ${
                    formData.categories.length === 0 ? 'border-red-300' : 'border-gray-200'
                  }`}>
                    {formData.categories.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {formData.categories.map((category) => (
                          <span
                            key={category}
                            className="inline-flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-primary-500 to-primary-600 text-white text-sm rounded-lg shadow-sm"
                          >
                            {category}
                            <button
                              onClick={() => removeCategory(category)}
                              className="text-white hover:text-primary-100 transition-colors"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {[
                        'Agricultural Supplies', 'Automotive', 'Bars & Pubs', 'Beauty & Personal Care',
                        'Business Services', 'Childcare', 'Construction', 'Education',
                        'Entertainment', 'Food & Beverage', 'Healthcare',
                        'Home & Garden', 'Manufacturing', 'Online Business', 'Real Estate',
                        'Restaurants', 'Retail', 'Technology', 'Transportation'
                      ].map((category) => (
                        <button
                          key={category}
                          onClick={() => handleCategoryChange(category)}
                          disabled={!formData.categories.includes(category) && formData.categories.length >= 3}
                          className={`text-left px-3 py-2 text-sm rounded-lg transition-all duration-200 ${
                            formData.categories.includes(category)
                              ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-md'
                              : formData.categories.length >= 3
                              ? 'text-gray-400 cursor-not-allowed bg-gray-100'
                              : 'text-gray-700 hover:bg-white hover:shadow-sm border border-gray-200'
                          }`}
                        >
                          {category}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-gray-600 mt-3 font-medium">
                      You have selected {formData.categories.length}/3 categories
                    </p>
                    {formData.categories.length === 0 && (
                      <p className="text-xs text-red-600 mt-1">Please select at least one category</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Select Region then City / Town */}
              <div id="section-region-city" className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Region then City / Town</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Region *
                    </label>
                    <select
                      value={formData.region}
                      onChange={(e) => handleInputChange('region', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                        !formData.region ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Select a region</option>
                      <option value="Alberta">Alberta</option>
                      <option value="British Columbia">British Columbia</option>
                      <option value="Manitoba">Manitoba</option>
                      <option value="New Brunswick">New Brunswick</option>
                      <option value="Newfoundland and Labrador">Newfoundland and Labrador</option>
                      <option value="Nova Scotia">Nova Scotia</option>
                      <option value="Ontario">Ontario</option>
                      <option value="Prince Edward Island">Prince Edward Island</option>
                      <option value="Quebec">Quebec</option>
                      <option value="Saskatchewan">Saskatchewan</option>
                      <option value="Northwest Territories">Northwest Territories</option>
                      <option value="Nunavut">Nunavut</option>
                      <option value="Yukon">Yukon</option>
                    </select>
                    <p className="text-xs text-gray-600 mt-1">
                      Select a region. Including the location of your business will increase the response rate.
                    </p>
                    {!formData.region && (
                      <p className="text-xs text-red-600 mt-1">Region is required</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      City / Town
                    </label>
                    <input
                      type="text"
                      value={formData.cityTown}
                      onChange={(e) => handleInputChange('cityTown', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Start typing city name..."
                    />
                    <p className="text-xs text-gray-600 mt-1">
                      Start typing the name of the town or city that the business is located in to see the full list, if the location is not listed we will add it.
                    </p>
                  </div>
                </div>
              </div>

              <AdditionalInformationForm
                formData={additionalInfoData}
                onInputChange={(field, value) => handleInputChange(field as any, value)}
                uploadedPhotos={uploadedPhotos}
                uploadedDocuments={uploadedDocuments}
                selectedFile={selectedFile}
                onFileSelect={setSelectedFile}
                onFileUpload={handleFileUpload}
                onRemovePhoto={handleRemovePhoto}
                onRemoveDocument={handleRemoveDocument}
                showIntroduction={true}
              />

              {/* REALTOR.ca Listing Section */}
              <div id="section-realtor" className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center mb-5">
                  <div className="w-1 h-6 bg-gradient-to-b from-slate-600 to-gray-700 rounded-full mr-3"></div>
                  <div className="flex items-center gap-3">
                    <img 
                      src="/assets/images/realtor-ca.png" 
                      alt="REALTOR.ca" 
                      className="h-10 w-auto"
                    />
                    <h3 className="text-lg font-semibold text-gray-900">Listing</h3>
                  </div>
                </div>
                
                <div className="space-y-4">
                  {selectedPackage === '6months' ? (
                    <>
                      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
                        <p className="text-sm text-blue-800">
                          <strong>REALTOR.ca Listing Included</strong>
                        </p>
                        <p className="text-sm text-blue-700 mt-2">
                          Since you selected the REALTOR package, a partnering real estate agent will submit your listing to REALTOR.ca and notify us of the MLS number.
                        </p>
                      </div>
                      <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-r-lg">
                        <p className="text-xs text-yellow-900 font-medium mb-2">
                          ⚠️ Important Reminder:
                        </p>
                        <p className="text-xs text-yellow-800">
                          The agent is <strong>NOT</strong> representing you and is <strong>NOT</strong> receiving a commission. They are simply facilitating the listing submission to REALTOR.ca.
                        </p>
                      </div>
                    </>
                  ) : (
                    <div className="text-sm text-gray-600">
                      <p>REALTOR.ca listing is available with select packages. Upgrade your package to include REALTOR.ca listing.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Step 2 (non-step 3) - keep original layout
    return (
      <div className="space-y-8">
        {/* Action Buttons */}
        <div className="flex justify-between items-center">
          {currentStep !== 3 && (
            <button
              onClick={prevStep}
              className="flex items-center gap-3 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-all duration-300 shadow-sm hover:shadow-md"
            >
              <ArrowLeftIcon className="h-5 w-5" />
              Back
            </button>
          )}
          {currentStep === 3 && <div></div>}
          <div className="flex items-center gap-4">
            <button
              onClick={saveForLater}
              disabled={isSaving}
              className="flex items-center gap-2 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-all duration-300 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                  Saving...
                </>
              ) : (
                <>
                  💾 Save For Later
                </>
              )}
            </button>
            <button
              onClick={currentStep === 3 ? submitListing : nextStep}
              disabled={isSaving || isSubmitting}
              className="flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 text-white rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Submitting...
                </>
              ) : isSaving ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Saving...
                </>
              ) : currentStep === 3 ? (
                'Submit Listing'
              ) : (
                <>
                  Continue Building Listing <ArrowRightIcon className="h-5 w-5" />
                </>
              )}
            </button>
          </div>
        </div>

        <AdditionalInformationForm
          formData={additionalInfoData}
          onInputChange={(field, value) => handleInputChange(field as any, value)}
          uploadedPhotos={uploadedPhotos}
          uploadedDocuments={uploadedDocuments}
          selectedFile={selectedFile}
          onFileSelect={setSelectedFile}
          onFileUpload={handleFileUpload}
          onRemovePhoto={handleRemovePhoto}
          onRemoveDocument={handleRemoveDocument}
          showIntroduction={true}
        />

        {/* Bottom Action Buttons */}
        <div className="flex justify-between items-center">
          {currentStep !== 3 && (
            <button
              onClick={prevStep}
              className="flex items-center gap-3 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-all duration-300 shadow-sm hover:shadow-md"
            >
              <ArrowLeftIcon className="h-5 w-5" />
              Back
            </button>
          )}
          {currentStep === 3 && <div></div>}
          <div className="flex items-center gap-4">
            <button
              onClick={saveForLater}
              disabled={isSaving}
              className="flex items-center gap-2 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-all duration-300 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                  Saving...
                </>
              ) : (
                <>
                  💾 Save For Later
                </>
              )}
            </button>
            <button
              onClick={submitListing}
              disabled={isSaving || isSubmitting}
              className="flex items-center gap-3 px-10 py-4 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-2xl font-bold text-lg transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                  Submitting...
                </>
              ) : (
                <>
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Submit Listing
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Show loading state while checking authentication (only if we're checking for existing drafts)
  if (loading && searchParams.get('draft')) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Render payment success page
  const renderPaymentSuccess = () => {
    const draftId = searchParams.get('draft');
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 flex items-start justify-center px-4 pt-12">
        <div className="max-w-2xl w-full bg-white rounded-2xl shadow-xl border border-gray-200/50 p-8 md:p-12">
          {/* Success Icon */}
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircleIcon className="w-12 h-12 text-green-600" />
            </div>
          </div>

          {/* Success Message */}
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Payment Successful! 🎉
            </h1>
            <p className="text-lg text-gray-700 mb-2">
              Your listing is paid and ready to complete!
            </p>
            <p className="text-sm text-gray-600">
              You'll be redirected to complete your listing in {countdown} seconds...
            </p>
          </div>

          {/* Action Button */}
          <div className="flex justify-center">
            <button
              onClick={() => {
                if (draftId) {
                  loadDraftAndGoToStep3(draftId);
                }
              }}
              className="px-8 py-4 bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 text-white rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-lg"
            >
              Complete my listing
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content="List your business for sale on BizBuyMax.com. Reach thousands of qualified buyers across North America. Start your listing today." />
        <meta name="keywords" content="sell business, list business for sale, business marketplace, Canada" />
      </Helmet>

      <style>{`
        @keyframes sectionHighlight {
          0% {
            background-color: rgba(59, 130, 246, 0.1);
            border-color: rgba(59, 130, 246, 0.3);
            box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.4);
          }
          50% {
            background-color: rgba(59, 130, 246, 0.15);
            border-color: rgba(59, 130, 246, 0.5);
            box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.2);
          }
          100% {
            background-color: transparent;
            border-color: transparent;
            box-shadow: 0 0 0 0 rgba(59, 130, 246, 0);
          }
        }
        .section-highlight {
          animation: sectionHighlight 2s ease-out;
          transition: all 0.3s ease;
        }
      `}</style>

      <main className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50">
        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {showPaymentSuccess ? renderPaymentSuccess() : (
            <>
              {currentStep === 1 && renderStep1()}
              {(currentStep === 2 || currentStep === 3) && renderStep2()}
            </>
          )}
        </div>
      </main>
    </>
  );
};

export default SellBusiness; 